import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Lots CAFÉCLIC : remplace l'ajout au panier du formulaire produit (bouton principal et barre fixe)
 * par l'ajout du lot sélectionné (1, 2 ou 3 exemplaires, couleur par exemplaire) + options cochées.
 */
class CcBundle extends HTMLElement {
  connectedCallback() {
    const data = JSON.parse(this.querySelector('[data-cc-bundle-data]')?.textContent || '{}');
    /** @type {Array<{id: number, title: string, price: number, available: boolean, image: string}>} */
    this.variants = data.variants || [];
    this.tiers = [...this.querySelectorAll('.cc-bundle__tier')];
    this.addons = this.querySelector('[data-cc-addons]');
    this.section = this.closest('.product-information') || document;
    this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';

    this.addEventListener('change', this.#onChange);
    this.tiers.forEach((tier) => tier.addEventListener('click', this.#onTierClick));
    // Sur window (capture) : passe avant la délégation d'événements d'Horizon, enregistrée sur document
    window.addEventListener('submit', this.#onSubmit, true);
    // Horizon recharge la variante de façon asynchrone : on attend sa réponse (événement productSelect)
    this.sectionEl = this.closest('.shopify-section') || document;
    this.sectionEl.addEventListener(StandardEvents.productSelect, this.#onMainVariantChange);

    const selected = this.tiers.find((t) => t.classList.contains('is-selected')) || this.tiers[0];
    this.select(selected);
  }

  disconnectedCallback() {
    window.removeEventListener('submit', this.#onSubmit, true);
    this.sectionEl.removeEventListener(StandardEvents.productSelect, this.#onMainVariantChange);
  }

  /** Variante choisie dans le sélecteur de couleur principal */
  get mainVariantId() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.section.querySelector('product-form-component input[name="id"]')
    );
    return this.currentVariantId || Number(input?.value) || this.variants[0]?.id;
  }

  get selectedTier() {
    return this.tiers.find((t) => t.classList.contains('is-selected'));
  }

  /** @param {HTMLElement | undefined} tier */
  select(tier) {
    if (!tier) return;
    this.tiers.forEach((t) => {
      const isSelected = t === tier;
      t.classList.toggle('is-selected', isSelected);
      const radio = /** @type {HTMLInputElement | null} */ (t.querySelector('.cc-bundle__radio-input'));
      if (radio) radio.checked = isSelected;
    });
    if (this.addons && this.addons.children.length) tier.append(this.addons);
    this.update();
  }

  /** Recalcule les prix affichés et le sous-total */
  update() {
    const variant = this.variants.find((v) => v.id === this.mainVariantId) || this.variants[0];
    if (!variant) return;
    const unit = variant.price;

    for (const tier of this.tiers) {
      const qty = Number(tier.dataset.quantity);
      const discount = Number(tier.dataset.discount);
      const full = unit * qty;
      // Même arrondi que les remises automatiques Shopify : remise par exemplaire, centimes tronqués
      const price = qty * (unit - Math.floor((unit * discount) / 100));
      const priceEl = tier.querySelector('[data-cc-price]');
      const compareEl = tier.querySelector('[data-cc-compare]');
      if (priceEl) priceEl.textContent = this.#format(price);
      if (compareEl) compareEl.textContent = this.#format(full);
      tier.dataset.total = String(price);
    }

    const addonsTotal = [...this.querySelectorAll('.cc-bundle__addon-input:checked')].reduce(
      (sum, input) => sum + Number(/** @type {HTMLInputElement} */ (input).dataset.price),
      0
    );
    const subtotal = this.querySelector('[data-cc-subtotal]');
    if (subtotal) subtotal.textContent = this.#format(Number(this.selectedTier?.dataset.total || 0) + addonsTotal);
  }

  /** Articles à ajouter : exemplaires du lot (regroupés par couleur) + options cochées */
  get items() {
    const tier = this.selectedTier;
    if (!tier) return [];
    /** @type {Map<number, number>} */
    const counts = new Map();
    const selects = [...tier.querySelectorAll('select[data-cc-unit]')];
    if (selects.length) {
      for (const select of selects) {
        const id = Number(/** @type {HTMLSelectElement} */ (select).value);
        counts.set(id, (counts.get(id) || 0) + 1);
      }
    } else {
      counts.set(this.mainVariantId, Number(tier.dataset.quantity) || 1);
    }
    for (const input of this.querySelectorAll('.cc-bundle__addon-input:checked')) {
      const id = Number(/** @type {HTMLInputElement} */ (input).value);
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    return [...counts].map(([id, quantity]) => ({ id, quantity }));
  }

  /** @param {Event} event */
  #onTierClick = (event) => {
    const tier = /** @type {HTMLElement} */ (event.currentTarget);
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('select, .cc-bundle__addon')) {
      if (!tier.classList.contains('is-selected')) this.select(tier);
      return;
    }
    this.select(tier);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches('.cc-bundle__radio-input')) {
      this.select(/** @type {HTMLElement} */ (target.closest('.cc-bundle__tier')));
    } else if (target.matches('select[data-cc-unit]')) {
      this.#syncUnitImage(/** @type {HTMLSelectElement} */ (target));
    }
    this.update();
  };

  /**
   * Quand la couleur principale change, les exemplaires non modifiés la suivent
   * @param {Event & {promise?: Promise<any>}} event
   */
  #onMainVariantChange = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (this.contains(target) || target.closest?.('product-card')) return;
    event.promise
      ?.then(({ detail } = {}) => {
        const id = Number(detail?.resource?.id);
        if (!id || !this.variants.some((v) => v.id === id)) return;
        this.currentVariantId = id;
        for (const select of this.querySelectorAll('select[data-cc-unit]:not([data-touched])')) {
          /** @type {HTMLSelectElement} */ (select).value = String(id);
          this.#syncUnitImage(/** @type {HTMLSelectElement} */ (select), false);
        }
        this.update();
      })
      .catch(() => {});
  };

  /**
   * @param {HTMLSelectElement} select
   * @param {boolean} [touched]
   */
  #syncUnitImage(select, touched = true) {
    if (touched) select.dataset.touched = 'true';
    const variant = this.variants.find((v) => v.id === Number(select.value));
    const img = /** @type {HTMLImageElement | null} */ (select.parentElement?.querySelector('.cc-bundle__variant-img'));
    if (img && variant?.image) {
      img.src = variant.image;
      img.hidden = false;
    }
  }

  /** Intercepte l'envoi du formulaire produit de cette page */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const productForm = form.closest('product-form-component');
    if (!productForm || !this.section.contains(productForm)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#addToCart(productForm);
  };

  /** @param {Element} productForm */
  async #addToCart(productForm) {
    const items = this.items;
    if (!items.length) return;
    const errorEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-cc-error]'));
    if (errorEl) errorEl.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    const routes = window.Theme?.routes || {};
    const deferred = CartLinesUpdateEvent.createPromise();
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    productForm.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(routes.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      const cart = await fetch(`${routes.cart_url || '/cart'}.json`).then((r) => r.json());

      if (result.status) {
        if (errorEl) {
          errorEl.textContent = result.description || result.message || 'Impossible d’ajouter ce lot au panier.';
          errorEl.hidden = false;
        }
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'cc-bundle', itemCount },
        });
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cc-bundle',
          sourceId: this.id || 'cc-bundle',
          itemCount,
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      if (errorEl) {
        errorEl.textContent = 'Erreur réseau, merci de réessayer.';
        errorEl.hidden = false;
      }
    }
  }

  /**
   * Formate un montant (centimes) avec le format monétaire de la boutique (ex. « €{{amount_with_comma_separator}} »).
   * @param {number} cents
   */
  #format(cents) {
    const fixed = (cents / 100).toFixed(2);
    const [int, dec] = fixed.split('.');
    const group = (sep) => int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const formats = {
      amount: `${group(',')}.${dec}`,
      amount_no_decimals: group(','),
      amount_with_comma_separator: `${group('.')},${dec}`,
      amount_no_decimals_with_comma_separator: group('.'),
      amount_with_space_separator: `${group(' ')},${dec}`,
      amount_with_apostrophe_separator: `${group("'")}.${dec}`,
    };
    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => formats[key] ?? formats.amount);
  }
}

if (!customElements.get('cc-bundle')) customElements.define('cc-bundle', CcBundle);
